import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  alignVariants,
  italicVariants,
  toneVariants,
  truncateVariants,
  typographyWeightVariants,
} from "./shared";

const labelVariants = cva("font-(family-name:--font-family-label)", {
  variants: {
    size: {
      lg: ["text-(length:--font-size-label-lg)", "leading-(--line-height-20)"],
      md: ["text-(length:--font-size-label-md)", "leading-(--line-height-20)"],
      sm: ["text-(length:--font-size-label-sm)", "leading-(--line-height-16)"],
      xs: ["text-(length:--font-size-label-xs)", "leading-(--line-height-16)"],
    },
    tone: toneVariants,
    align: alignVariants,
    truncate: truncateVariants,
    weight: typographyWeightVariants,
    italic: italicVariants,
  },
  defaultVariants: {
    size: "md",
    tone: "default",
    weight: "semibold",
    italic: false,
  },
});

type LabelElement = "span" | "label" | "p";

export interface LabelProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof labelVariants> {
  /** Override the default HTML element (defaults to span) */
  as?: LabelElement;
  /** Associates the label with a form control (only applicable when as="label") */
  htmlFor?: string;
}

/**
 * Label component for UI labels, form field labels, tags, and captions.
 * Uses Inter font family with semibold weight.
 *
 * Sizes: large (14px), medium (13px), small (12px), extra-small (11px).
 */
function Label({
  size = "md",
  tone,
  align,
  truncate,
  weight = "semibold",
  italic = false,
  as = "span",
  ...props
}: LabelProps) {
  return React.createElement(as, {
    className: labelVariants({
      size,
      tone,
      align,
      truncate,
      weight,
      italic,
    }),
    ...props,
  });
}

export { Label };
